import { useCallback, useMemo, useState } from 'react';

import { Box, Flex, IconButton } from '@chakra-ui/react';
import { usePathname } from 'next/navigation';
// react-icons
import { AiOutlineMore } from 'react-icons/ai';
import { FiMenu } from 'react-icons/fi';
import { useAccount } from 'wagmi';

// components
import SubHeader from '@/components/Header/SubHeader';
import { SUPPORT, TERMS_OF_SERVICE } from '@/constants/analytics';
import { BINANCE_WALLET_IDS, STADER_URL } from '@/constants/constants';
import { StrapiSubHeaderOption } from '@/customtypes/global';
import {
  HeaderWalletConnect,
  trackEvent,
  useDappSDKProvider,
} from '@/dapp-sdk-v2';
import BaseImage from '@/dapp-sdk-v2/components/Image';
import useIsMobile from '@/hooks/useMobile';
import { useCMSData } from '@/providers/CMSDataProvider';
import { HeaderContainer } from '@/themes';

import NetworkSwitch from './NetworkSwitch';

interface HeaderProps {
  isEmbed?: boolean;
}
const Header: React.FC<HeaderProps> = ({ isEmbed = false }) => {
  const { isSupported, isMobile } = useIsMobile();
  const pathname = usePathname();

  const { address, connector: activeConnector } = useAccount();

  const [isSubMenuVisible, setIsSubMenuVisible] = useState(false);

  const { setStakeData } = useDappSDKProvider();
  const setMobileMenuVisible = (isVisible: boolean) => {
    setStakeData({
      isMobileMenuOpen: isVisible,
    });
  };

  const pageData = useCMSData((data) => data.page);

  const { sub_header_options } = pageData || { data: [] };

  const showNetworkSwitch = useMemo(
    () =>
      address &&
      !isEmbed &&
      !BINANCE_WALLET_IDS.includes(activeConnector?.id ?? '') &&
      pathname === '/stake/',
    [address, isEmbed, activeConnector, pathname],
  );

  const subHeaderOptions: StrapiSubHeaderOption[] = useMemo(() => {
    const options = sub_header_options?.data;
    return options?.map((data) => data?.attributes || []) ?? [];
  }, [sub_header_options]);

  const headerMenuClick = useCallback((key: string) => {
    let eventName = key;
    let eventParams = {};
    switch (key) {
      case 'tos': {
        eventName = TERMS_OF_SERVICE;
        eventParams = {
          type: 'header',
        };
        break;
      }
      case 'support': {
        eventName = SUPPORT;
        break;
      }
    }
    if (eventName) {
      trackEvent(eventName, { ...eventParams });
    }
  }, []);

  const showWalletConnect = useMemo(() => {
    if (!isSupported && isMobile) return false;
    return true;
  }, [isSupported, isMobile]);

  return (
    <>
      <HeaderContainer>
        <Box display={{ base: 'inherit', xl: 'none' }} alignItems="center">
          <IconButton
            display={{ base: 'flex', xl: 'none' }}
            onClick={() => setMobileMenuVisible(true)}
            aria-label="open menu"
            h="32px"
            w="32px"
            bg="transperent"
            color="textPrimary"
            icon={<FiMenu />}
          />
          <Box pl="1rem">
            <a href={STADER_URL}>
              <BaseImage
                src="/assets/images/stader-icon.svg"
                width={24}
                height={24}
                alt="stader-logo"
              />
            </a>
          </Box>
        </Box>

        <Box height={{ xl: '56px' }}>
          <Flex
            alignItems="center"
            position={{ xl: 'fixed' }}
            right="0px"
            zIndex="1"
          >
            {showNetworkSwitch && <NetworkSwitch />}
            {showWalletConnect && <HeaderWalletConnect isEmbed={isEmbed} />}

            <IconButton
              variant="icon"
              alignItems="center"
              icon={<AiOutlineMore height="24px" width="24px" />}
              onClick={() => setIsSubMenuVisible(true)}
              aria-label="change theme"
              fontSize="20px"
              ml="4px"
              borderRadius="0.5rem"
              display={{ base: 'flex', md: 'none' }}
            />

            <SubHeader
              headerOptins={subHeaderOptions}
              shouldDisplayMenu={isSubMenuVisible}
              onChangeDisplayMenu={(isVisible) =>
                setIsSubMenuVisible(isVisible)
              }
              onSubHeaderMenuClick={headerMenuClick}
            />
          </Flex>
        </Box>
      </HeaderContainer>
    </>
  );
};

export default Header;
